// spots-ids.js
// API communication layer for fetching spots by IDs
// Handles all communication with spots-ids.php endpoint

import { getCurrentLanguage } from '../translations/translations.js';
import { fetchWithCache } from './cache/data-cache.js';

// Default chunk size for API requests (should match backend limit)
const DEFAULT_CHUNK_SIZE = 10;

/**
 * Splits an array into chunks of specified size
 * @param {Array} array - The array to split
 * @param {number} chunkSize - Size of each chunk
 * @returns {Array} Array of chunks
 */
function chunkArray(array, chunkSize = DEFAULT_CHUNK_SIZE) {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
}

/**
 * Fetches spots by IDs using the unified spots-ids.php endpoint.
 * This is the main API function that handles all ID-based spot requests.
 * @param {string[]} ids - Array of spot IDs to fetch
 * @param {Object} options - Optional parameters
 * @param {boolean} options.favorites - Whether this is a favorites request
 * @param {string} options.lang - Language code (default: 'en')
 * @param {number} options.chunkSize - Chunk size for large requests
 * @returns {Promise<Array>} Array of spots
 */
export async function fetchSpotsByIds(ids, options = {}) {
    if (!Array.isArray(ids) || ids.length === 0) {
        return [];
    }

    const {
        favorites = false,
        lang = getCurrentLanguage(),
        chunkSize = DEFAULT_CHUNK_SIZE,
        map_id = null,
        areaSlug = null
    } = options;

    // For large requests, use chunking
    if (ids.length > chunkSize) {
        return await fetchSpotsInChunks(ids, { favorites, lang, chunkSize, map_id, areaSlug });
    }

    // For smaller requests, use direct API call
    const params = new URLSearchParams();
    params.append('ids', ids.join(','));

    if (favorites) {
        params.append('favorites', 'true');
    }

    if (lang) {
        params.append('lang', lang);
    }

    if (map_id) {
        params.append('map_id', map_id.toString());
    }

    if (areaSlug) {
        params.append('area_slug', areaSlug);
    }

    try {
        const data = await fetchWithCache(`/app_core/api/sqlite/spots-ids.php?${params}`);

        // Handle both old format (array) and new format (object with spots property)
        if (Array.isArray(data)) {
            return data;
        } else if (data.spots) {
            return data.spots;
        } else {
            return [];
        }
    } catch (error) {
        console.error('Error fetching spots by IDs:', error);
        throw error;
    }
}

/**
 * Fetches spots by IDs in chunks to handle large lists
 * @param {Array} spotIds - Array of spot IDs to fetch
 * @param {Object} options - Options for chunking
 * @param {boolean} options.favorites - Whether this is a favorites request
 * @param {string} options.lang - Language code
 * @param {number} options.chunkSize - Size of each chunk
 * @returns {Promise<Array>} Array of all spots
 */
export async function fetchSpotsInChunks(spotIds, options = {}) {
    if (spotIds.length === 0) return [];

    const {
        favorites = false,
        lang = getCurrentLanguage(),
        chunkSize = DEFAULT_CHUNK_SIZE,
        map_id = null,
        areaSlug = null
    } = options;

    const chunks = chunkArray(spotIds, chunkSize);
    const allSpots = [];

    for (const chunk of chunks) {
        try {
            const spots = await fetchSpotsByIds(chunk, { favorites, lang, map_id, areaSlug });
            allSpots.push(...spots);
        } catch (error) {
            console.error('Error fetching spots chunk:', error);
            // Continue with other chunks even if one fails
        }
    }

    return allSpots;
}

/**
 * Fetches a single spot by ID
 * @param {string|number} id - Single spot ID
 * @param {Object} options - Optional parameters
 * @param {string} options.lang - Language code (default: 'en')
 * @returns {Promise<Object|null>} Single spot object or null if not found
 */
export async function fetchSingleSpot(id, options = {}) {
    if (!id) return null;

    const spots = await fetchSpotsByIds([id.toString()], options);
    return spots.length > 0 ? spots[0] : null;
}

/**
 * Fetches spots with favorites-specific handling
 * This is a convenience function for favorites functionality
 * @param {Array} favIds - Array of favorite IDs
 * @param {Object} options - Options for fetching
 * @returns {Promise<Array>} Array of favorite spots
 */
export async function fetchFavoritesSpots(favIds, options = {}) {
    if (favIds.length === 0) return [];

    // For favorites, we can use a larger chunk size as configured in backend
    const FAVORITES_CHUNK_SIZE = 50;

    // If the list is small enough, try the favorites endpoint first
    if (favIds.length <= FAVORITES_CHUNK_SIZE) {
        try {
            return await fetchSpotsByIds(favIds, { ...options, favorites: true });
        } catch (error) {
            console.error('Error fetching favorites:', error);
        }
    }

    // Fallback to chunking if favorites endpoint fails or list is too large
    return await fetchSpotsInChunks(favIds, { ...options, favorites: true, chunkSize: DEFAULT_CHUNK_SIZE });
} 